import {
  PokeTable,
  PokeTableBody,
  PokeTableCell,
  PokeTableCellWithCopy,
  PokeTableRow,
} from "@app/components/poke/shadcn/ui/table";
import type { DataRetentionConfig } from "@app/lib/data_retention";
import { getMetronomeCustomerUrl } from "@app/lib/metronome/urls";
import { usePokeWorkOSDSyncStatus } from "@app/lib/swr/poke";
import type { WorkOSConnectionSyncStatus } from "@app/lib/types/workos";
import type { ExtensionConfigurationType } from "@app/types/extension";
import { isDevelopment } from "@app/types/shared/env";
import { asDisplayName } from "@app/types/shared/utils/string_utils";
import type { WorkspaceType } from "@app/types/user";
import type { WorkspaceDomain } from "@app/types/workspace";
import { Chip, LinkWrapper } from "@dust-tt/sparkle";

export function WorkspaceInfoTable({
  owner,
  membersCount,
  inactiveMembersCount,
  metronomeCustomerId,
  stripeCustomerId,
  workspaceVerifiedDomains,
  workspaceCreationDay,
  extensionConfig,
  dataRetention,
  workosEnvironmentId,
  hasDummyFeature,
  temporalFrontNamespace,
}: {
  owner: WorkspaceType;
  membersCount: number;
  inactiveMembersCount: number;
  metronomeCustomerId: string | null;
  stripeCustomerId: string | null;
  workspaceVerifiedDomains: WorkspaceDomain[];
  workspaceCreationDay: string;
  extensionConfig: ExtensionConfigurationType | null;
  dataRetention: DataRetentionConfig | undefined;
  workosEnvironmentId: string;
  hasDummyFeature: boolean;
  temporalFrontNamespace: string;
}) {
  const { dsyncStatus } = usePokeWorkOSDSyncStatus({ owner });

  const getStatusChipColor = (status: WorkOSConnectionSyncStatus["status"]) => {
    switch (status) {
      case "configured":
        return "success";
      case "configuring":
        return "warning";
      case "not_configured":
        return "info";
      default:
        return "info";
    }
  };

  const getConnectionStateChipColor = (state: string) => {
    switch (state) {
      case "active":
        return "success";
      case "inactive":
      case "deleting":
      case "invalid_credentials":
        return "warning";
      case "validating":
        return "warning";
      case "draft":
        return "highlight";
      default:
        return "info";
    }
  };

  const nbAgentsWithRetention = dataRetention
    ? Object.entries(dataRetention.agents).length
    : 0;

  return (
    <div className="flex justify-between gap-3">
      <div className="flex flex-grow flex-col rounded-lg border p-4 pb-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-md flex-grow pb-4 font-bold">Workspace info</h2>
        </div>
        <PokeTable>
          <PokeTableBody>
            <PokeTableRow>
              <PokeTableCell>Id</PokeTableCell>
              <PokeTableCellWithCopy label={owner.id.toString()} />
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>sId</PokeTableCell>
              <PokeTableCellWithCopy label={owner.sId} />
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>Workspace Health</PokeTableCell>
              <PokeTableCell>
                <LinkWrapper
                  href={`https://metabase.dust.tt/dashboard/34-snowflake-workspace-health?end_date=2030-12-31&start_date=2024-01-01&tab=30-executive-summary&workspace_size_difference_margin=0.2&workspacesid=${owner.sId}`}
                  target="_blank"
                  className="text-xs text-highlight-400"
                >
                  Metabase
                </LinkWrapper>
              </PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>WorkOS dashboard</PokeTableCell>
              <PokeTableCell>
                {owner.workOSOrganizationId && (
                  <LinkWrapper
                    href={`https://dashboard.workos.com/${workosEnvironmentId}/organizations/${owner.workOSOrganizationId}`}
                    target="_blank"
                    className="text-xs text-highlight-400"
                  >
                    {owner.workOSOrganizationId}
                  </LinkWrapper>
                )}
              </PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>Stripe customer</PokeTableCell>
              <PokeTableCell>
                {stripeCustomerId ? (
                  <LinkWrapper
                    href={
                      isDevelopment()
                        ? `https://dashboard.stripe.com/test/customers/${stripeCustomerId}`
                        : `https://dashboard.stripe.com/customers/${stripeCustomerId}`
                    }
                    target="_blank"
                    className="text-xs text-highlight-400"
                  >
                    {stripeCustomerId}
                  </LinkWrapper>
                ) : (
                  "Not provisioned"
                )}
              </PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>Metronome</PokeTableCell>
              <PokeTableCell>
                {metronomeCustomerId ? (
                  <LinkWrapper
                    href={getMetronomeCustomerUrl(metronomeCustomerId)}
                    target="_blank"
                    className="text-xs text-highlight-400"
                  >
                    {metronomeCustomerId}
                  </LinkWrapper>
                ) : (
                  "Not provisioned"
                )}
              </PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>Creation</PokeTableCell>
              <PokeTableCell>{workspaceCreationDay}</PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>Members count</PokeTableCell>
              <PokeTableCell>
                {`${membersCount} active, ${inactiveMembersCount} inactive`}
              </PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>SSO Enforced</PokeTableCell>
              <PokeTableCell>{owner.ssoEnforced ? "✅" : "❌"}</PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>Auto Join</PokeTableCell>
              <PokeTableCell>
                {workspaceVerifiedDomains.length > 0 &&
                workspaceVerifiedDomains.every((d) => d.domainAutoJoinEnabled)
                  ? "✅"
                  : workspaceVerifiedDomains.some(
                        (d) => d.domainAutoJoinEnabled
                      )
                    ? "⚠️"
                    : "❌"}
              </PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>Verified Domains</PokeTableCell>
              <PokeTableCell className="max-w-sm break-words">
                {workspaceVerifiedDomains.map((d) => d.domain).join(", ")}
              </PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell className="max-w-48">
                Extension blacklisted domains/URLs
              </PokeTableCell>
              <PokeTableCell className="max-w-sm break-words">
                {extensionConfig?.blacklistedDomains.length
                  ? extensionConfig.blacklistedDomains.join(", ")
                  : "None"}
              </PokeTableCell>
            </PokeTableRow>
            <PokeTableRow>
              <PokeTableCell>Directory Sync</PokeTableCell>
              <PokeTableCell>
                <Chip
                  color={getStatusChipColor(
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    dsyncStatus?.status || "not_configured"
                  )}
                >
                  {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                  {asDisplayName(dsyncStatus?.status || "not_configured")}
                </Chip>
              </PokeTableCell>
            </PokeTableRow>
            {dsyncStatus?.connection && (
              <PokeTableRow>
                <PokeTableCell>Directory Sync State</PokeTableCell>
                <PokeTableCell>
                  <Chip
                    color={getConnectionStateChipColor(
                      dsyncStatus.connection.state
                    )}
                  >
                    {dsyncStatus.connection.state}
                  </Chip>
                </PokeTableCell>
              </PokeTableRow>
            )}
            {dataRetention !== undefined && (
              <>
                <PokeTableRow>
                  <PokeTableCell>✂️ Conversations retention</PokeTableCell>
                  <PokeTableCell>
                    {dataRetention.conversations !== null
                      ? `${dataRetention.conversations} days inactive`
                      : "No retention policy"}
                  </PokeTableCell>
                </PokeTableRow>
                <PokeTableRow>
                  <PokeTableCell>✂️ Workspace retention</PokeTableCell>
                  <PokeTableCell>
                    {dataRetention.workspace} days post downgrade
                  </PokeTableCell>
                </PokeTableRow>
                <PokeTableRow>
                  <PokeTableCell>✂️ Agents retention</PokeTableCell>
                  <PokeTableCell>
                    {nbAgentsWithRetention === 0
                      ? "No agents with policies"
                      : `${nbAgentsWithRetention} with policies`}
                  </PokeTableCell>
                </PokeTableRow>
              </>
            )}
            <PokeTableRow>
              <PokeTableCell>Self-Improving Skills</PokeTableCell>
              <PokeTableCell>
                <LinkWrapper
                  href={`https://cloud.temporal.io/namespaces/${temporalFrontNamespace}/schedules?query=%60ScheduleId%60%3D%22reinforcement-workspace-${owner.sId}%22`}
                  target="_blank"
                  className="text-xs text-highlight-400"
                >
                  Schedule
                </LinkWrapper>
              </PokeTableCell>
            </PokeTableRow>
            {hasDummyFeature && (
              <PokeTableRow>
                <PokeTableCell>Dummy feature</PokeTableCell>
                <PokeTableCell>Enabled</PokeTableCell>
              </PokeTableRow>
            )}
          </PokeTableBody>
        </PokeTable>
      </div>
    </div>
  );
}
